import { Button } from '@sureco/design-system'
import { Modal } from '@surecompanies/core_components'
import { message } from 'antd'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import React, { useState } from 'react'
import { trackCensusFileRowsUpdates } from '../../api/census-enrollment-platform-api-client'
import { CensusFileRowDto } from '../../api/dto/census-file-row.dto'
import { CensusFileRowStatusEnum } from '../../api/enums/census-file-row-status.enum'
import { CensusFileRowGridData } from '../../types/census-file-row-grid-data'
import { mapGridDataToCensusFileRowDto } from '../../utils/map-grid-data-to-census-file-row-dto'

interface TrackSelectedUpdatesButtonProps {
  publicKey: string
  selectedRows: CensusFileRowGridData[]
  handleSubmit: (rows: CensusFileRowGridData[]) => void
  setLoading: (loading: boolean) => void // Prop to update loading state in parent
}

const sendForTracking = async (
  rowsToSubmit: CensusFileRowGridData[],
  publicKey: string
) => {
  const acceptedRows: CensusFileRowDto[] = rowsToSubmit.map((gridRow) => ({
    ...mapGridDataToCensusFileRowDto(gridRow),
    status: CensusFileRowStatusEnum.IN_REVIEW,
  }))
  const clientName =
    rowsToSubmit.length > 0 ? rowsToSubmit[0].rowData.ClientName : ''
  const finalRows = acceptedRows.map((row) => ({
    ...row,
    rowData: {
      ...row.rowData,
      ClientName: clientName,
    },
  }))
  await trackCensusFileRowsUpdates(finalRows, publicKey)
}

const TrackSelectedUpdatesButton = ({
  selectedRows,
  publicKey,
  handleSubmit,
  setLoading,
}: TrackSelectedUpdatesButtonProps) => {
  const [loading, setButtonLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const confirmTracking = async () => {
    let timer: NodeJS.Timeout | null = null
    let shouldShowLoading = false

    // Show delayed loading state
    timer = setTimeout(() => {
      setButtonLoading(true)
      setLoading(true)
      shouldShowLoading = true
    }, 800)

    try {
      await sendForTracking(selectedRows, publicKey)
      message.success(
        `${selectedRows.length} records sent to zendesk successfully`,
        3
      )
      postActivityLog({
        action: ActivityLogAction.CENSUS_REVIEW_SEND_TO_ZENDESK,
        description: `sent ${selectedRows.length} census review records to zendesk`,
        additionalData: {
          publicKey,
          recordCount: selectedRows.length,
          rows: selectedRows,
        },
      })
    } catch (error) {
      message.error(`Failed to submit records: '${(error as Error).message}'`)
    } finally {
      if (shouldShowLoading) {
        setButtonLoading(false)
        setLoading(false)
      }
      if (timer) clearTimeout(timer)

      setShowConfirmModal(false)
      handleSubmit(selectedRows)
    }
  }

  if (selectedRows.length === 0) return null

  return (
    <>
      <Button
        onClick={() => setShowConfirmModal(true)}
        size="large"
        variant="black"
      >
        {loading ? 'Submitting...' : `Send to Zendesk (${selectedRows.length})`}
      </Button>

      <Modal
        open={showConfirmModal}
        title="Confirm Tracking"
        onCancel={() => setShowConfirmModal(false)}
        onOk={confirmTracking}
        okText="Yes, Continue"
        cancelText="No, Cancel"
      >
        <p style={{ textAlign: 'center' }}>Are you sure?</p>
        <p style={{ textAlign: 'center' }}>
          Sending To Zendesk will open Zendesk tickets for these records, but
          they will not be updated in the system until someone reviews the
          ticket and takes action.
        </p>
      </Modal>
    </>
  )
}

export default TrackSelectedUpdatesButton
